import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Description,
  FieldError,
  Form,
  Heading,
  Input,
  Label,
  Link,
  TextField,
} from '@heroui/react';
import { AsyncButton } from '../../../components/ui/async-button';
import { sendPasswordReset } from '../../../lib/firebase';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { AuthSplitLayout } from '../components/auth-split-layout';
import { validateEmail } from '../utils/validate-email';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>();

  useEffect(() => {
    const stateEmail = (location.state as { email?: string } | null)?.email;
    if (stateEmail) setEmail(stateEmail);
  }, [location.state]);

  useEffect(() => {
    if (!attemptedSubmit) return;
    setEmailError(validateEmail(email) ?? undefined);
  }, [email, attemptedSubmit]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    setAttemptedSubmit(true);
    const error = validateEmail(email);
    setEmailError(error ?? undefined);
    if (error) return;

    setSubmitting(true);
    try {
      await sendPasswordReset(email.trim());

      localStorage.removeItem('firebase:authUser');
      sessionStorage.clear();

      showSuccess(
        'Verifique sua caixa de entrada para redefinir sua senha.',
        'Email enviado!',
      );

      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao enviar email');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthSplitLayout>
      <div className="w-full max-w-sm space-y-6">
        <div className="flex w-full flex-col items-center space-y-1 text-center lg:items-start lg:text-left">
          <Heading level={2} className="w-full text-center text-2xl font-semibold lg:text-left">
            Recuperar senha
          </Heading>
          <Description className="text-center lg:text-left">
            Digite seu email para receber um link de recuperação.
          </Description>
        </div>

        <Form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <TextField
            name="email"
            type="email"
            fullWidth
            isInvalid={attemptedSubmit && !!emailError}
          >
            <Label>Email</Label>
            <Input
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              autoComplete="email"
              className="h-11 text-base"
            />
            {attemptedSubmit && emailError ? <FieldError>{emailError}</FieldError> : null}
          </TextField>

          <AsyncButton type="submit" variant="primary" size="lg" fullWidth isPending={submitting}>
            Enviar email de recuperação
          </AsyncButton>
        </Form>

        <p className="text-muted text-center text-sm lg:text-left">
          Lembrou da senha?{' '}
          <Link className="text-sm font-medium text-accent" onPress={() => navigate('/login')}>
            Fazer login
          </Link>
        </p>
      </div>
    </AuthSplitLayout>
  );
}
